import { useEffect, useMemo, useRef, useState } from 'react'
import { useLibraryDeps } from '../library/LibraryDeps'

/**
 * What each fan in the list spent on this Account, from the same OnlyFans
 * profile call the fan panel makes (users/profile → subscribedOnData), so the
 * badge and the panel agree. users/list, which names the list, carries no
 * spend at all.
 *
 * One call per fan, so they go out behind the list (it never waits on them),
 * a few at a time and in list order, and each answer is kept for the session.
 * A fan is asked again when their chat moves on (a new message can be a tip
 * or an unlocked PPV).
 */

/** Profile calls in flight at once: OnlyFans rate-limits bursts. */
export const SPEND_CONCURRENCY = 3

interface SpendCache {
  /** Spend by fan id; null when OnlyFans did not say. */
  spent: Map<string, number | null>
  /** The chat's last-message key each answer belongs to. */
  key: Map<string, string>
  queued: Set<string>
  queue: { fanId: string; key: string }[]
  active: number
  listeners: Set<() => void>
}

/** One per gateway (one per open Account), so another tab or a revisit shows at once. */
const caches = new WeakMap<object, SpendCache>()

type Raw = Record<string, unknown>
const obj = (v: unknown): Raw => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : {})

/** The fan's total spend in a users/profile answer (useFan readProfile). */
export function spendFromProfile(body: unknown): number | null {
  const d = obj(obj(body).data ?? body)
  const user = obj(d.user ?? d.profile ?? d)
  const sub = obj(user.subscribedOnData)
  for (const v of [sub.totalSumm, user.totalSpent, user.total_spent]) {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN
    if (Number.isFinite(n) && n >= 0) return n
  }
  return null
}

export function useFanSpend(fans: { fanId: string; key: string }[]): Map<string, number | null> {
  const { gateway } = useLibraryDeps()
  const cache = useMemo(() => {
    let c = caches.get(gateway)
    if (!c) caches.set(gateway, (c = { spent: new Map(), key: new Map(), queued: new Set(), queue: [], active: 0, listeners: new Set() }))
    return c
  }, [gateway])
  const [version, setVersion] = useState(0)
  const bump = useRef(() => setVersion((v) => v + 1))

  useEffect(() => {
    const listener = () => bump.current()
    cache.listeners.add(listener)
    return () => void cache.listeners.delete(listener)
  }, [cache])

  useEffect(() => {
    const pump = () => {
      while (cache.active < SPEND_CONCURRENCY && cache.queue.length) {
        const { fanId, key } = cache.queue.shift()!
        cache.active++
        gateway<unknown>('/v1/users/profile', { fan_id: fanId })
          .then((body) => {
            cache.spent.set(fanId, spendFromProfile(body))
            cache.key.set(fanId, key)
          })
          // A failed ask is retried the next time the list asks for this fan.
          .catch(() => undefined)
          .finally(() => {
            cache.active--
            cache.queued.delete(fanId)
            cache.listeners.forEach((l) => l())
            pump()
          })
      }
    }
    // New asks go in list order, ahead of any older ones still waiting.
    const fresh = fans.filter((f) => /^[1-9]\d*$/.test(f.fanId) && !cache.queued.has(f.fanId) && cache.key.get(f.fanId) !== f.key)
    if (!fresh.length) return
    fresh.forEach((f) => cache.queued.add(f.fanId))
    cache.queue.unshift(...fresh)
    pump()
  }, [fans, cache, gateway])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => new Map(cache.spent), [cache, version])
}
